import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { startKCLogin } from '../Auth/keycloakAuth';
import { FetchCompaniesByEmail_api } from './LoginAPI';
import { AppConstant } from '../../config/AppConstants';
import { Images } from '../Common/Images';
import { EMAIL_PATTERN } from '../../utils/CommonUtils';

// Shared OIDC IdPs (one Keycloak IdP each; company resolved downstream by email domain).
const KC_IDP_MAP = { microsoft: 'microsoft', gmail: 'google' };

// SAML providers are PER-COMPANY Keycloak IdPs (alias `{ssotype}-{companyGuid}`), provisioned by
// the sso:sync-keycloak-saml-idps command. The company guid comes from the discovery method row.
const SAML_PROVIDERS = ['okta', 'onelogin', 'duo', 'saml', 'microsoft-saml'];

function idpHintFor(method, selectedCompanyId) {
  if (KC_IDP_MAP[method.ssotype]) {
    return KC_IDP_MAP[method.ssotype];
  }
  if (SAML_PROVIDERS.includes(method.ssotype)) {
    const guid =
      method.CompanyID || method.companyID || method.CompanyId || method.companyId || selectedCompanyId || '';
    return guid ? `${method.ssotype}-${guid}` : null;
  }
  return null;
}

const METHOD_LABELS = {
  microsoft: 'Continue with Microsoft',
  gmail: 'Continue with Google',
  okta: 'Continue with Okta',
  onelogin: 'Continue with OneLogin',
  duo: 'Continue with Duo',
  saml: 'Continue with SSO',
  manual: 'Sign in with password',
};

function parseDiscoveryResponse(res) {
  const data = res?.Data || res?.data || {};
  const records = data?.Records || data?.records || {};
  const responseCode = data?.ResponseCode ?? data?.responseCode;
  const rawCompanies = records?.Companies || records?.companies || [];
  const rawMethods = records?.LoginMethods || records?.loginMethods || [];

  const companies = rawCompanies.map((c) => ({
    id: c?.id ?? c?.Id ?? c?.CompanyId ?? c?.CompanyID ?? '',
    name: c?.CompanyName || c?.companyName || c?.Name || c?.name || '',
  }));

  const methods = rawMethods.map((m) => ({
    ...m,
    ssotype: String(m?.ssotype ?? m?.SSOType ?? '').toLowerCase(),
  }));

  return { responseCode, companies, methods };
}

const s = {
  page: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', background: '#f3f3f3', fontFamily: 'inherit',
  },
  card: {
    textAlign: 'center', background: '#fff', borderRadius: 16,
    padding: '40px 48px', boxShadow: '0 4px 28px rgba(0,0,0,0.09)',
    width: 360, maxWidth: '90vw',
  },
  logo: { width: 56, marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 700, color: '#222', marginBottom: 4 },
  subtitle: { fontSize: 13, color: '#888', marginBottom: 24 },
  input: {
    width: '100%', padding: '10px 14px', border: '1px solid #ddd',
    borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: 'border-box',
  },
  btn: {
    width: '100%', padding: '11px 0', background: '#01a08c', color: '#fff',
    border: 'none', borderRadius: 30, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', marginBottom: 8,
  },
  methodBtn: {
    width: '100%', padding: '11px 0', background: '#fff', color: '#333',
    border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontWeight: 500,
    cursor: 'pointer', marginBottom: 10, display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 8,
  },
  methodBtnKC: {
    background: '#f7f9ff', borderColor: '#c5d0e8',
  },
  companyItem: {
    padding: '10px 14px', border: '1px solid #eee', borderRadius: 8,
    cursor: 'pointer', marginBottom: 6, fontSize: 14, color: '#333',
    background: '#fafafa', textAlign: 'left',
  },
  error: { color: '#ef3826', fontSize: 13, marginTop: 4, marginBottom: 8 },
  info: { color: '#888', fontSize: 12, marginTop: 8 },
  link: { fontSize: 13, color: '#01a08c', cursor: 'pointer', textDecoration: 'underline' },
};

export default function KCLoginPage() {
  const history = useHistory();
  const [step, setStep] = useState('email');   // email | company | methods | redirecting
  const [email, setEmail] = useState('');
  const [companies, setCompanies] = useState([]);
  const [methods, setMethods] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMethods = async (trimmedEmail, companyId = '') => {
    setLoading(true);
    setError(null);

    try {
      const res = await FetchCompaniesByEmail_api({
        ClientId: AppConstant.DEFAULT_CLIENT_ID,
        PrimaryEmail: trimmedEmail,
        CompanyId: companyId,
      });

      const { responseCode, companies: fetchedCompanies, methods: fetchedMethods } =
        parseDiscoveryResponse(res);

      if (responseCode !== 200) {
        setError('Could not find your account. Please check your email.');
        setLoading(false);
        return;
      }

      // Multiple companies and none selected yet — show picker
      if (!companyId && fetchedCompanies.length > 1) {
        setCompanies(fetchedCompanies);
        setStep('company');
        setLoading(false);
        return;
      }

      if (!fetchedMethods.length) {
        setError('No login methods found for this account.');
        setLoading(false);
        return;
      }

      setMethods(fetchedMethods);
      setStep('methods');
      setLoading(false);
    } catch (err) {
      console.error('KCLoginPage fetchMethods error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    fetchMethods(trimmed);
  };

  const handleCompanySelect = (company) => {
    setSelectedCompanyId(company.id);
    fetchMethods(email.trim(), company.id);
  };

  const handleMethodSelect = async (method) => {
    const kcIdp = idpHintFor(method, selectedCompanyId);
    if (kcIdp) {
      setStep('redirecting');
      await startKCLogin(kcIdp, email.trim(), selectedCompanyId);
      return;
    }
    // Non-KC method — guide user to main login page
    setError(`${METHOD_LABELS[method.ssotype] || method.ssotype} is not available here. Please use the main login page.`);
  };

  if (step === 'redirecting') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <img src={Images.RonspotSquareLogo} alt="Ronspot" style={s.logo} />
          <img src={Images.LoaderFull} alt="" style={{ width: 40, display: 'block', margin: '0 auto 12px' }} />
          <p style={{ color: '#888', fontSize: 14, margin: 0 }}>Redirecting to your identity provider…</p>
        </div>
      </div>
    );
  }

  if (step === 'company') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <img src={Images.RonspotSquareLogo} alt="Ronspot" style={s.logo} />
          <p style={s.title}>Select your organisation</p>
          <p style={s.subtitle}>{email}</p>
          {companies.map((c) => (
            <div key={c.id} style={s.companyItem} onClick={() => handleCompanySelect(c)}>
              {c.name}
            </div>
          ))}
          {error && <p style={s.error}>{error}</p>}
          <p style={s.info}>
            <span style={s.link} onClick={() => { setStep('email'); setError(null); setCompanies([]); }}>
              ← Use a different email
            </span>
          </p>
        </div>
      </div>
    );
  }

  if (step === 'methods') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <img src={Images.RonspotSquareLogo} alt="Ronspot" style={s.logo} />
          <p style={s.title}>Choose how to sign in</p>
          <p style={s.subtitle}>{email}</p>
          {methods.map((m, i) => {
            const isKC = Boolean(idpHintFor(m, selectedCompanyId));
            return (
              <button
                key={i}
                style={{ ...s.methodBtn, ...(isKC ? s.methodBtnKC : {}) }}
                onClick={() => handleMethodSelect(m)}
              >
                {METHOD_LABELS[m.ssotype] || m.ssotype}
              </button>
            );
          })}
          {error && <p style={s.error}>{error}</p>}
          <p style={s.info}>
            <span style={s.link} onClick={() => { setStep('email'); setError(null); setMethods([]); }}>
              ← Use a different email
            </span>
          </p>
        </div>
      </div>
    );
  }

  // step === 'email'
  return (
    <div style={s.page}>
      <div style={s.card}>
        <img src={Images.RonspotSquareLogo} alt="Ronspot" style={s.logo} />
        <p style={s.title}>Sign in to Ronspot</p>
        <p style={s.subtitle}>Enter your work email to continue</p>
        <form onSubmit={handleEmailSubmit}>
          <input
            style={s.input}
            type="email"
            placeholder="Work email address"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(null); }}
            autoFocus
            disabled={loading}
          />
          {error && <p style={s.error}>{error}</p>}
          <button
            type="submit"
            style={{ ...s.btn, ...(loading ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
            disabled={loading}
          >
            {loading ? 'Looking up…' : 'Continue'}
          </button>
        </form>
        <p style={s.info}>
          Not the new flow?{' '}
          <span style={s.link} onClick={() => history.replace('/login')}>Use main login</span>
        </p>
      </div>
    </div>
  );
}
